// The provider sign-in of a store app (mobile-apps.md §6, R2927).
//
// Google refuses an OAuth sign-in in an embedded WKWebView. The iOS app
// shell therefore runs the flow in `ASWebAuthenticationSession`: the page
// sends the `signin` op with the start path, with `app=ios` added, and
// waits. The app shell then loads the handoff URL, which sets the session
// cookie and opens the page again. A browser and the Android TWA go to the
// start path as before.
//
// The sheet holds the cookie jar of Safari and not the jar of the page. A
// page with a session (a guest or a registered account) therefore asks a
// start ticket first and adds it to the start path, so the start route
// knows the account of the page (R2938).

import {
  ApiError, CODE_REAUTH_REQUIRED, oauthStartPath, oauthStartTicket, type SignInLink,
} from '../net/api';
import { bridgeHas, bridgeSend, pageWindow, type BridgeWindow } from './bridge';

/** The query that tells the meta server the sign-in runs in the iOS app
 *  shell (mobile-apps.md §6). */
export const APP_PARAM = 'app';
export const APP_IOS = 'ios';
/** The query that carries the start ticket (R2938). */
export const TICKET_PARAM = 'ticket';

/** The start path with `app=ios` added, and the start ticket when there
 *  is one. */
export function iosStartPath(path: string, ticket?: string): string {
  const url = new URL(path, 'http://x');
  url.searchParams.set(APP_PARAM, APP_IOS);
  if (ticket) url.searchParams.set(TICKET_PARAM, ticket);
  return url.pathname + url.search;
}

/** How one sign-in starts. Every field is optional. */
export interface OAuthStart {
  /** The deep link the landing screen carries (R1659). */
  link?: SignInLink;
  /** True when the page holds a session: a guest or a registered
   *  account. The iOS app shell then asks a start ticket first. */
  session?: boolean;
  /** The navigation of a browser; a test passes its own. */
  go?: (path: string) => void;
  /** The window of the bridge; a test passes a fake shell. */
  bridge?: BridgeWindow | null;
  /** The ticket call; a test passes its own. */
  ticket?: () => Promise<string>;
}

/**
 * Starts a sign-in or a link with `provider`. It answers false when the
 * start ticket failed: nothing opens, and the caller shows the sentence of
 * a dead sign-in state. It answers true when the sheet or the navigation
 * started. A ticket that met `reauth_required` (R2963) rejects with that
 * refusal, so the caller shows SIGN IN AGAIN and not the dead state.
 */
export async function startOAuth(provider: string, o: OAuthStart = {}): Promise<boolean> {
  const path = oauthStartPath(provider, o.link);
  const bridge = o.bridge !== undefined ? o.bridge : pageWindow();
  if (bridgeHas('signin', bridge)) {
    let ticket: string | undefined;
    if (o.session) {
      try {
        ticket = await (o.ticket ?? oauthStartTicket)();
      } catch (e) {
        if (e instanceof ApiError && e.code === CODE_REAUTH_REQUIRED) throw e;
        return false;
      }
    }
    if (bridgeSend('signin', { provider, path: iosStartPath(path, ticket) }, bridge)) return true;
  }
  (o.go ?? ((p: string) => location.assign(p)))(path);
  return true;
}
